import React, { useEffect, useMemo, useState } from 'react';
import { Mail, Paperclip, Send } from 'lucide-react';
import { useI18n } from '../../context/I18nContext';
import Modal, { ModalFooter } from '../ui/Modal';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { buildEmailBodies, plainTextToEmailHtml } from '../../utils/messageVariables';
import { prepareEmailSignatureHtml } from '../../utils/emailSignatureHtml';
import { formatStorageFromBytes } from '../../utils/storageQuota';

export type GmailSendModalPayload = {
  to: string;
  subject: string;
  includeSignature: boolean;
};

export type GmailSendAttachmentPreview = {
  id: string;
  file_name: string;
  size_bytes?: number | null;
};

interface GmailSendModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (payload: GmailSendModalPayload) => void | Promise<void>;
  loading?: boolean;
  initialTo: string;
  initialSubject: string;
  bodyPreview: string;
  emailSignature?: string;
  emailSignatureHtml?: string;
  leadName?: string;
  fromEmail?: string | null;
  attachments?: GmailSendAttachmentPreview[];
}

const GmailSendModal: React.FC<GmailSendModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  loading = false,
  initialTo,
  initialSubject,
  bodyPreview,
  emailSignature = '',
  emailSignatureHtml = '',
  leadName,
  fromEmail,
  attachments = [],
}) => {
  const { t, language } = useI18n();
  const [to, setTo] = useState(initialTo);
  const [subject, setSubject] = useState(initialSubject);
  const [toError, setToError] = useState<string | null>(null);
  const [subjectError, setSubjectError] = useState<string | null>(null);
  const [includeSignature, setIncludeSignature] = useState(true);
  const [cleanSignatureHtml, setCleanSignatureHtml] = useState('');

  const hasSignature = Boolean(emailSignature.trim() || emailSignatureHtml.trim());

  useEffect(() => {
    if (!isOpen) return;
    setTo(initialTo);
    setSubject(initialSubject);
    setToError(null);
    setSubjectError(null);
    setIncludeSignature(hasSignature);
  }, [isOpen, initialTo, initialSubject, hasSignature]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    const raw = emailSignatureHtml.trim();
    if (!raw) {
      setCleanSignatureHtml('');
      return;
    }
    void prepareEmailSignatureHtml(raw).then((html) => {
      if (!cancelled) setCleanSignatureHtml(html);
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, emailSignatureHtml]);

  const previewBodies = useMemo(
    () =>
      buildEmailBodies({
        body: bodyPreview,
        signatureText: emailSignature,
        signatureHtml: cleanSignatureHtml,
        includeSignature: includeSignature && hasSignature,
      }),
    [bodyPreview, emailSignature, cleanSignatureHtml, includeSignature, hasSignature]
  );

  const previewHtml = useMemo(() => {
    const html =
      previewBodies.bodyHtml ||
      plainTextToEmailHtml(previewBodies.bodyText) ||
      '';
    return html.trim();
  }, [previewBodies.bodyHtml, previewBodies.bodyText]);

  const handleConfirm = async () => {
    const nextTo = to.trim();
    const nextSubject = subject.trim();
    let valid = true;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nextTo)) {
      setToError(t('messages.gmailInvalidEmail'));
      valid = false;
    } else {
      setToError(null);
    }
    if (!nextSubject) {
      setSubjectError(t('messages.gmailSubjectRequired'));
      valid = false;
    } else {
      setSubjectError(null);
    }
    if (!valid) return;
    await onConfirm({
      to: nextTo,
      subject: nextSubject,
      includeSignature: includeSignature && hasSignature,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={loading ? () => undefined : onClose}
      title={t('messages.gmailSendModalTitle')}
      size="xl"
      showCloseButton={!loading}
      footer={
        <ModalFooter>
          <Button type="button" variant="ghost" size="md" onClick={onClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            variant="primary"
            size="md"
            onClick={() => void handleConfirm()}
            loading={loading}
            icon={<Send className="w-4 h-4" />}
          >
            {t('messages.gmailSendConfirm')}
          </Button>
        </ModalFooter>
      }
    >
      <div className="space-y-5">
        <div className="flex items-start gap-3 rounded-xl border border-primary-100 bg-primary-50/50 px-3.5 py-3">
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-500/10 text-primary-600">
            <Mail className="h-4 w-4" aria-hidden />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900">
              {leadName
                ? t('messages.gmailSendModalLead', { name: leadName })
                : t('messages.gmailSendModalSubtitle')}
            </p>
            {fromEmail ? (
              <p className="mt-0.5 text-xs text-gray-500 truncate">
                {t('messages.gmailSendFrom', { email: fromEmail })}
              </p>
            ) : (
              <p className="mt-0.5 text-xs text-gray-500">{t('messages.gmailSendNowHint')}</p>
            )}
          </div>
        </div>

        <Input
          label={t('messages.gmailRecipient')}
          type="email"
          autoComplete="email"
          value={to}
          onChange={(e) => {
            setTo(e.target.value);
            if (toError) setToError(null);
          }}
          error={toError || undefined}
          disabled={loading}
          placeholder="contact@marque.com"
        />

        <Input
          label={t('messages.subject')}
          value={subject}
          onChange={(e) => {
            setSubject(e.target.value);
            if (subjectError) setSubjectError(null);
          }}
          error={subjectError || undefined}
          disabled={loading}
          placeholder={t('messages.emailSubject')}
        />

        {hasSignature ? (
          <button
            type="button"
            role="switch"
            aria-checked={includeSignature}
            disabled={loading}
            onClick={() => setIncludeSignature((v) => !v)}
            className="flex w-full items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-3.5 py-3 text-left transition-colors hover:border-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="min-w-0">
              <span className="block text-sm font-medium text-gray-900">
                {t('messages.gmailIncludeSignature')}
              </span>
            </span>
            <span
              aria-hidden
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                includeSignature ? 'bg-primary-500' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                  includeSignature ? 'translate-x-5' : 'translate-x-0.5'
                }`}
              />
            </span>
          </button>
        ) : null}

        {attachments.length > 0 ? (
          <div>
            <p className="mb-1.5 text-sm font-medium text-gray-700">
              {t('messages.gmailAttachments')}
              <span className="ml-1.5 font-normal text-gray-500 tabular-nums">
                ({attachments.length})
              </span>
            </p>
            <ul className="flex flex-col gap-1.5">
              {attachments.map((file) => {
                const sizeLabel =
                  typeof file.size_bytes === 'number' && file.size_bytes > 0
                    ? formatStorageFromBytes(file.size_bytes, language)
                    : null;
                return (
                  <li
                    key={file.id}
                    className="flex items-center gap-2 min-w-0 rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-2"
                  >
                    <Paperclip className="w-3.5 h-3.5 shrink-0 text-gray-400" aria-hidden />
                    <span className="min-w-0 flex-1 truncate text-sm text-gray-800">
                      {file.file_name}
                    </span>
                    {sizeLabel ? (
                      <span className="shrink-0 text-xs text-gray-500 tabular-nums">
                        {sizeLabel}
                      </span>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}

        {previewHtml ? (
          <div>
            <p className="mb-1.5 text-sm font-medium text-gray-700">{t('messages.gmailPreview')}</p>
            <div
              className="rounded-xl border border-gray-200 bg-white px-3.5 py-3 [&_a]:text-[#1155cc] [&_a]:underline [&_img]:!h-auto [&_img]:max-w-full"
              style={{
                fontFamily: 'Arial, Helvetica, sans-serif',
                fontSize: '14.666666666666666px',
                color: '#000',
                lineHeight: 'normal',
                wordWrap: 'break-word',
              }}
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          </div>
        ) : null}
      </div>
    </Modal>
  );
};

export default GmailSendModal;
